import React, {useEffect, useMemo, useState} from 'react';
import {Modal} from "@material-ui/core";
import ProgressCheck from "../../../style/icons/ProgressCheck/progress_check.svg";
import CheckIconGreen from "../../../style/icons/CheckSign/CheckSign-green.svg";
import CloseRed from "../../../style/icons/CloseRed/close-red.svg";
import InfoIconBlue from "../../../style/icons/InfoSign/info-sign-blue.svg";
import './ProgressModal.scss';
import {compose} from "redux";
import {connect} from "react-redux";
import {getAllProcesses} from "../../../redux/processes/selectors";
import ProgressBar from "../ProgressBar/ProgressBar";
import {getFormValues} from "redux-form";
import {useTranslation} from "react-i18next";
import processAPI from "../../../api/process";
import ErrorIcon from '@material-ui/icons/Error';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import moment from 'moment';

const ProgressModal = (
  {
    user,
    processes
  }) => {

  const {t} = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState({});

  // Start checking processes when component mounts
  useEffect(() => {
    processAPI.startCheckProcesses();
  }, []);

  // Force refresh when modal is open
  useEffect(() => {
    if (isOpen) {
      processAPI.updateProcessesData();
    }
  }, [isOpen]);

  const toggleJobExpanded = (jobId) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobId]: !prev[jobId]
    }));
  };

  const precessesMemo = useMemo(() => {
    return processes.map((process) => {
      const filteredProcess = {
        id: process.id,
        status: process.status,
        isError: !!process.statistic?.errors?.length,
        errors: process.statistic?.errors || [],
        message: process.statistic?.message || '',
        percent: process.percentCompleted,
        createdAt: process.createdAt,
        startedAt: process.startedAt,
        completedAt: process.completedAt,
        totalRows: process.statistic?.total_rows || 0,
        type: process.jobType,
        statistic: process.statistic || {}
      }

      switch (process.jobType) {
        case 'user_subscription': {
          filteredProcess.name = t('subscribeProcess');
          break;
        }
        case 'promocode_import': {
          filteredProcess.name = t('importPromoCodeProcess');
          break;
        }
        case 'push_send': {
          filteredProcess.name = t('pushNotificationProcess');
          break;
        }
        default: {
          filteredProcess.name = process.jobType || 'Unknown process';
        }
      }
      
      const isExpanded = expandedJobs[process.id] || false;
      
      return (
        <ProgressItem 
          key={process.id}
          process={filteredProcess}
          isExpanded={isExpanded}
          onToggleExpand={() => toggleJobExpanded(process.id)}
        />
      );
    })
  }, [processes, expandedJobs, t])

  if (!user?.roles) return null;

  if (!user?.roles?.includes('ROLE_ADMIN')) {
    return null;
  }

  if (!processes.length) return null;

  const openHandler = () => {
    setIsOpen(true);
  }
  const closeHandler = () => {
    setIsOpen(false);
  }

  return (
    <div>
      <img
        className='progress-modal_img'
        onClick={openHandler}
        src={ProgressCheck}
        alt=""
      />
      <Modal
        open={isOpen}
        onClose={closeHandler}
      >
        <div className='progress-modal_body'>
          <div className="progress-modal_content">
            <div className='progress-modal_content_text'>{t('titleProcess')}</div>
            <div className='progress-modal_content_list'>
              {precessesMemo}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return moment(dateString).format('DD.MM.YYYY HH:mm:ss');
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <img src={CheckIconGreen} alt="Completed" className="progress-modal_item_status_icon" />;
    case 'started':
      return <img src={InfoIconBlue} alt="In Progress" className="progress-modal_item_status_icon" />;
    default:
      return null;
  }
};

const getTranslatedStatus = (status, t) => {
  switch (status) {
    case 'completed':
      return t('processStatusCompleted');
    case 'started':
      return t('processStatusStarted');
    default:
      return status;
  }
};

const getTranslatedProcessName = (processName, t) => {
  switch (processName) {
    case 'consultation_export':
      return t('processTypeConsultationExport');
    case 'user_subscription':
      return t('processTypeUserSubscription');
    case 'promocode_import':
      return t('processTypePromocodeImport');
    case 'push_send':
      return t('processTypePushSend');
    default:
      return processName;
  }
};

const ProgressItem = ({process, isExpanded, onToggleExpand}) => {
  const {t} = useTranslation();
  
  return (
    <div className='progress-modal_item'>
      <div className='progress-modal_item_header'>
        <div className='progress-modal_item_name'>
          {getTranslatedProcessName(process.name, t)} #{process.id}
          {process.isError && (
            <ErrorIcon className="error-icon" color="error" fontSize="small" />
          )}
        </div>
        <div className='progress-modal_item_status'>
          {getStatusIcon(process.status)}
          <span className={`progress-modal_item_status_text progress-modal_item_status_${process.status}`}>
            {getTranslatedStatus(process.status, t)}
          </span>
        </div>
        <button 
          className="expand-button" 
          onClick={onToggleExpand}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </button>
      </div>
      
      <ProgressBar stopAnimation={true} percent={process.percent} />
      <div className='progress-modal_item_message'>
        {process.message} ~ {process.percent}%
      </div>
      
      {isExpanded && (
        <div className='progress-modal_item_details'>
          <div className='progress-modal_item_detail'>
            <span className='progress-modal_item_detail_label'>{t('processCreatedAt')}:</span>
            <span className='progress-modal_item_detail_value'>{formatDate(process.createdAt)}</span>
          </div>
          <div className='progress-modal_item_detail'>
            <span className='progress-modal_item_detail_label'>{t('processStartedAt')}:</span>
            <span className='progress-modal_item_detail_value'>{formatDate(process.startedAt)}</span>
          </div>
          <div className='progress-modal_item_detail'>
            <span className='progress-modal_item_detail_label'>{t('processCompletedAt')}:</span>
            <span className='progress-modal_item_detail_value'>{formatDate(process.completedAt)}</span>
          </div>
          <div className='progress-modal_item_detail'>
            <span className='progress-modal_item_detail_label'>{t('processTotalRows')}:</span>
            <span className='progress-modal_item_detail_value'>{process.totalRows || 0}</span>
          </div>
        </div>
      )}
      
      {process.isError && process.errors.length > 0 && (
        <div className='progress-modal_item_errors'>
          <div className='progress-modal_item_errors_header'>
            <img src={CloseRed} alt="Error" className="progress-modal_item_error_icon" />
            <span>{t('processErrors')}:</span>
          </div>
          {process.errors.map((error, index) => (
            <div key={index} className='progress-modal_item_error'>
              {typeof error === 'object' && error.row ? (
                <>
                  <div className="progress-modal_item_error_location">
                    <span className="progress-modal_item_error_row">{t('processErrorRow')} {error.row}</span>
                    {error.column && (
                      <span className="progress-modal_item_error_column">{t('processErrorColumn')}: {error.column}</span>
                    )}
                  </div>
                  <div className="progress-modal_item_error_text">{error.message}</div>
                </>
              ) : (
                error
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = ({user, ...state}) => ({
  processes: getAllProcesses(state),
  user
});


const enhance = compose(
  connect(mapStateToProps),
);

export default enhance(ProgressModal);